import { useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useState } from 'react';
import { useAsync } from 'react-async';
import { getTrip } from '../../../api/trip';
import htmlToPdf, { prepareDocumentForHtmlToPdf } from '@koob/html-to-pdf';
import KoobLayout from '../../../components/Koob/KoobLayout';
import ReceiptLoading from '@koob/booking/src/components/receipt/ReceiptLoading';
import ReceiptWrapper from '@koob/booking/src/components/receipt/ReceiptWrapper';
import ReceiptHeader from '@koob/booking/src/components/receipt/ReceiptHeader';
import ReceiptContent from '@koob/booking/src/components/receipt/ReceiptContent';
import { dayjs, formatDate } from '@koob/dates';
import { KoobContainer, KoobButton } from '@koob/ui';
import ReceiptNotes from '@koob/booking/src/components/receipt/ReceiptNotes';
import ReceiptTravelDiaryPriceDisplay from '@koob/booking/src/components/receipt/ReceiptTravelDiaryPriceDisplay';
import ReceiptFooter from '@koob/booking/src/components/receipt/ReceiptFooter';
import { useTranslation } from 'react-i18next';
import { fetchTripReceiptData } from './utilities';
import { ReceiptTypeEnum } from '@koob/booking/src/components/receipt/ReceiptTypeEnum';
import { AdminBookingStateEnum } from '@koob/enums';
import { ReceiptProductTypeEnum } from '@koob/booking/src/components/receipt/ReceiptProductTypeEnum';
import {
  useGlobalMarkup,
  useIsPricingSummaryDirty,
  useIsPricingSummarySaving,
  useKoobStore,
  usePricingAdditionalInformations,
  usePricingServices,
  usePricingDisplayMode,
  useTravelDiaryPriceDisplay,
} from 'store';

export default function TripReceipt() {
  const { tripId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('trips');

  const globalMarkup = useGlobalMarkup();
  const services = usePricingServices();
  const displayMode = usePricingDisplayMode();
  const additionalInformations = usePricingAdditionalInformations();
  const isSaving = useIsPricingSummarySaving();
  const isDirty = useIsPricingSummaryDirty();
  const initPricingSummary = useKoobStore(state => state.initPricingSummary);
  const setServices = useKoobStore(state => state.setPricingServices);
  const setAdditionalInformations = useKoobStore(
    state => state.setPricingAdditionalInformations,
  );
  const setGlobalMarkup = useKoobStore(state => state.setGlobalMarkup);
  const savePricingSummary = useKoobStore(state => state.savePricingSummary);
  const setDisplayMode = useKoobStore(state => state.setPricingDisplayMode);
  const travelDiaryPriceDisplay = useTravelDiaryPriceDisplay();
  const setTravelDiaryPriceDisplay = useKoobStore(
    state => state.setTravelDiaryPriceDisplay,
  );
  const fetchCurrencies = useKoobStore(state => state.fetchCurrencies);
  const currencies = useKoobStore(state => state.currencies);
  const isLoadingCurrencies = useKoobStore(state => state.isLoadingCurrencies);
  const targetCurrency = useKoobStore(state => state.targetCurrency);
  const exchangeRate = useKoobStore(state => state.exchangeRate);
  const updatePricingSummary = useKoobStore(
    state => state.updatePricingSummary,
  );

  const [processingExport, setProcessingExport] = useState(false);

  const fetchAndInitReceipt = useCallback(
    async ({ tripId, versionId }) => {
      const [, tripResponse] = await Promise.all([
        fetchCurrencies(),
        getTrip({ tripId, versionId }),
      ]);

      const trip = tripResponse?.data;
      if (!trip) return null;

      const { bookings, experiences, hotels } = await fetchTripReceiptData({
        trip,
      });

      const travelersCount =
        trip?.adultPax + (trip?.childrenBirthdates?.length ?? 0) ??
        trip?.maxPax;

      const bookedItems = bookings
        ?.sort((a, b) => dayjs(a.startAt).unix() - dayjs(b.startAt).unix())
        ?.reduce((acc, booking) => {
          if (booking?.state === AdminBookingStateEnum.REPLACED) return acc;

          if (booking?.experiencesBooked?.length) {
            const experience = booking.experiencesBooked[0];

            acc.push({
              id: booking.bookingId,
              type: experience.experience?.type,
              name: experience.experience?.name,
              location: `${experience?.experience?.inCity?.title}${
                experience?.experience?.inCity?.country?.alpha2
                  ? ', ' + experience?.experience?.inCity?.country?.alpha2
                  : ''
              }`,
              cancellationPolicy: experience.experience?.cancellationPolicies,
              state: booking.state,
              startAt: booking.startAt,
              endAt: booking.endAt,
              pax: experience.travelers.length,
              price:
                parseFloat(experience.price) -
                (experience.extras?.reduce((acc, extra) => {
                  if (
                    extra.state === AdminBookingStateEnum.CANCELLED ||
                    extra.state === AdminBookingStateEnum.REFUSED
                  )
                    return acc;
                  return acc + parseFloat(extra.price);
                }, 0) || 0),
              currency: booking.currency,
            });

            experience?.extras?.forEach(extra => {
              if (extra && extra.state !== AdminBookingStateEnum.REPLACED) {
                acc.push({
                  // KD-3650 — a service retained twice on a day is two booked
                  // lines sharing the extra's id: keying the receipt on that
                  // id made them one row, which then took the markup, the
                  // edits and the removal of its twin.
                  id: extra.extraCompositionId ?? extra.id,
                  type: ReceiptProductTypeEnum.EXTRA,
                  name: extra.name,
                  state: extra.state,
                  startAt: formatDate(
                    dayjs(booking.startAt).add(extra.dayIndex, 'days'),
                    'YYYY-MM-DD',
                  ),
                  endAt: formatDate(
                    dayjs(booking.startAt).add(extra.dayIndex, 'days'),
                    'YYYY-MM-DD',
                  ),
                  pax: extra.adultPax + (extra.childrenBirthdates?.length || 0),
                  price: parseFloat(extra.price),
                  currency: booking.currency,
                });
              }
            });
          }

          if (booking?.hotel) {
            const hotel = booking.hotel;

            acc.push({
              id: booking.bookingId,
              type: ReceiptProductTypeEnum.HOTEL,
              name: hotel.displayName,
              location: `${hotel.city?.title}, ${hotel.city?.country?.title}`,
              cancellationPolicy: hotel.cancelationPolicies,
              state: booking.state,
              startAt: booking.startAt,
              endAt: booking.endAt,
              pax: booking.roomsBooked?.reduce(
                (acc, room) => acc + room.travelers.length,
                0,
              ),
              price: booking.roomsBooked?.reduce(
                (acc, room) =>
                  acc +
                  parseFloat(room?.room?.bedChoice?.totalPriceWithPromo || 0),
                0,
              ),
              cancellationFee: parseFloat(booking.cancellationFees || 0),
              currency: booking.currency,
            });
          }

          return acc;
        }, []);

      const draftItems = trip?.tripDays?.reduce((acc, day) => {
        if (day.experience) {
          const experience = experiences.find(
            exp => exp.id === day.experience.id,
          );

          if (experience && day.experienceDayIndex === 0) {
            acc.push({
              id: experience.id,
              type: experience.type,
              name: experience.name,
              location: `${experience.inCity?.title}${
                experience.inCity?.country?.alpha2
                  ? ', ' + experience.inCity?.country?.alpha2
                  : ''
              }`,
              cancellationPolicy: experience.cancellationPolicies,
              state: 'draft',
              startAt: formatDate(
                dayjs(trip.startDate).add(day.dayIndex - 1, 'days'),
                'YYYY-MM-DD',
              ),
              endAt: formatDate(
                dayjs(trip.startDate).add(
                  day.dayIndex - 2 + day.experience.durationDays,
                  'days',
                ),
                'YYYY-MM-DD',
              ),
              pax: travelersCount,
              price: experience?.price,
              currency: experience?.currency,
            });
          }

          day.options?.forEach(option => {
            // KD-3650 — every occurrence of a service carries the same
            // experience id, so matching on it alone read them all back as the
            // first one: each row then showed the first occurrence's price,
            // and the whole party as its pax count. Options saved before this
            // ticket carry no key and are still matched on the id alone.
            const experience =
              experiences.find(
                exp =>
                  exp.id === option.optionId &&
                  (exp.occurrenceKey ?? null) ===
                    (option.occurrenceKey ?? null),
              ) ?? experiences.find(exp => exp.id === option.optionId);

            if (experience) {
              const optionPax =
                (experience.composition?.adults ?? 0) +
                (experience.composition?.childrenBirthdates?.length ?? 0);

              acc.push({
                id: experience.occurrenceKey
                  ? `${experience.id}-${experience.occurrenceKey}`
                  : experience.id,
                type: 'extra',
                name: experience.name,
                state: 'draft',
                startAt: formatDate(
                  dayjs(trip.startDate).add(day.dayIndex - 1, 'days'),
                  'YYYY-MM-DD',
                ),
                endAt: formatDate(
                  dayjs(trip.startDate).add(day.dayIndex - 1, 'days'),
                  'YYYY-MM-DD',
                ),
                pax: travelersCount,
                price: experience?.price,
                currency: experience?.currency,
              });
            }
          });
        }

        const program = experiences?.find(exp => exp.id === day.experience.id)
          ?.programs?.[day.experienceDayIndex];

        if (day.hotelId && !program?.isIncludedAccomodation) {
          const hotel = hotels.find(h => h.id === day.hotelId);

          if (hotel) {
            acc.push({
              id: hotel.id,
              type: 'hotel',
              name: hotel.displayName,
              location: `${hotel.city?.title}, ${hotel.country?.title}`,
              cancellationPolicy: hotel.cancelationPolicies,
              state: 'draft',
              startAt: formatDate(
                dayjs(trip.startDate).add(day.dayIndex - 1, 'days'),
                'YYYY-MM-DD',
              ),
              endAt: formatDate(
                dayjs(trip.startDate).add(day.dayIndex, 'days'),
                'YYYY-MM-DD',
              ),
              pax: travelersCount,
              price: hotel?.price,
              currency: hotel?.currency,
            });
          }
        }

        return acc;
      }, []);

      const calculatedItems =
        (bookedItems?.length ? bookedItems : draftItems) ?? [];

      if (calculatedItems.length > 0) {
        await initPricingSummary('trip', tripId, calculatedItems);
      }

      return { trip };
    },
    [initPricingSummary, fetchCurrencies],
  );

  const {
    data: receiptData,
    isLoading: isAsyncLoading,
    error,
  } = useAsync({
    promiseFn: fetchAndInitReceipt,
    tripId,
    versionId: searchParams.get('version'),
    watch: tripId,
  });

  const trip = receiptData?.trip;
  const loading = isAsyncLoading || processingExport;

  const exportToPdf = async () => {
    setProcessingExport(true);

    const html = prepareDocumentForHtmlToPdf(document.cloneNode(true));

    await htmlToPdf({
      html,
      options: {
        scale: 0.8,
        margin: {
          top: '1.5cm',
          bottom: '1.5cm',
          left: '0cm',
          right: '0cm',
        },
      },
      filename: `trip-receipt-${trip?.id}-${trip?.name
        ?.replace(/\s+/g, '-')
        ?.toLowerCase()}`,
    });

    setProcessingExport(false);
  };

  if (error) {
    return (
      <KoobLayout>
        <KoobContainer>
          <div className="max-w-6xl mx-auto py-20 text-center">
            <h2 className="text-xl font-semibold">
              {t('receipt.error.title')}
            </h2>
            <p className="mt-2 text-gray-500">
              {t('receipt.error.description')}
            </p>
          </div>
        </KoobContainer>
      </KoobLayout>
    );
  }

  return (
    <KoobLayout>
      {loading && <ReceiptLoading type={ReceiptTypeEnum.TRIP} />}

      <KoobContainer>
        <div className="mb-3 max-w-6xl mx-auto flex justify-end">
          <KoobButton
            variant="primary"
            onClick={exportToPdf}
            loading={processingExport}
          >
            <i className="far fa-cloud-download mr-1" />
            {t('receipt.download')}
          </KoobButton>
        </div>

        <ReceiptWrapper>
          <ReceiptHeader type={ReceiptTypeEnum.TRIP} />

          <ReceiptContent
            items={services}
            setItems={setServices}
            markup={globalMarkup}
            setMarkup={setGlobalMarkup}
            type={ReceiptTypeEnum.TRIP}
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
            currencyConversion={{
              currencies,
              targetCurrency,
              exchangeRate,
              disabled: isLoadingCurrencies,
            }}
            onUpdatePricingSummary={updatePricingSummary}
          />

          <ReceiptTravelDiaryPriceDisplay
            isTravelDiaryPriceDisplayed={
              travelDiaryPriceDisplay.isTravelDiaryPriceDisplayed
            }
            travelDiaryPriceDisplayLevel={
              travelDiaryPriceDisplay.travelDiaryPriceDisplayLevel
            }
            setTravelDiaryPriceDisplay={setTravelDiaryPriceDisplay}
          />

          <ReceiptNotes
            additionalInformations={additionalInformations}
            setAdditionalInformations={setAdditionalInformations}
          />
        </ReceiptWrapper>

        {!loading && (
          <ReceiptFooter
            isSaving={isSaving}
            isSaveDisabled={!isDirty}
            onCancel={() => window.history.back()}
            onSave={() => savePricingSummary(window.history.back())}
          />
        )}
      </KoobContainer>
    </KoobLayout>
  );
}
